import { Nullable } from "@core/domain/types/nullable.type";
import { Undefinable } from "@core/domain/types/undefinable.type";
import {
    DepositAnotherSourcesFinancingTypeEnum,
    DepositOwnFinancingTypeEnum,
    DepositSubventionTypeEnum,
    DepositTypeEnum,
} from "@entity/domain/models/deposit-type.model";
import { IdentificationTypeEnum } from "./identification-type.model";
import { PaymentMethodTypeEnum } from "@entity/domain/models/payment-method.model";
import { IvaExemptTypeEnum } from "@entity/domain/models/iva-exempt-type.model";
import { IrpfRetentionTypeEnum } from "./irpf-retention-type.model";
import { IrpfRetentionPercentageTypeEnum } from "./irpf-retention-percentage-type.model";
import { ReasonTypeEnum } from "./reason-type.model";

export class SplitDeposit {
    constructor(
        public readonly id: number,
        public depositId: number,
        public projectId: number,
        public assignedPercentage: number,
    ) { }
}

export class CreateSplitDeposit {
    constructor(
        public depositId: number,
        public projectId: number,
        public assignedPercentage: number,
    ) { }
}

export class CreateDeposit {
    // eslint-disable-next-line max-params
    constructor(
        public concept: string,
        public incomeLinkedAnotherProject: Nullable<number>,
        public amount: number,
        public name: string,
        public depositDate: string,
        public paymentMethod: Nullable<PaymentMethodTypeEnum>,
        public ivaExemptionType: Nullable<IvaExemptTypeEnum>,
        public irpfRetentionType: Nullable<IrpfRetentionTypeEnum>,
        public irpfRetentionPercentageType: Nullable<IrpfRetentionPercentageTypeEnum>,
        public reasonType: Nullable<ReasonTypeEnum>,
        public identificationType: Nullable<IdentificationTypeEnum>,
        public identification: Nullable<string>,
        public street: Nullable<string>,
        public recipientIdentificationType: Nullable<IdentificationTypeEnum>,
        public recipientIdentification: Nullable<string>,
        public recipientStreet: Nullable<string>,
        public financingOrganization: Nullable<string>,
        public issuer: Nullable<string>,
        public recipient: Nullable<string>,
        public otherPaymentSource: Nullable<string>,
        public expedientNumber: Nullable<number>,
        public depositYear: string,
        public tax: number,
        public baseAmount: number,
        public entity: number,
        public relatedProject?: number,
        public relatedDocument?: number,
        public communicationDate?: string,
        public invoiceIssueDate?: string,
        public resolutionDate?: string,
        public executionDate?: string,
        public othersType?: string,
        public observations?: string,
        public attachedDocumentName?: string,
        // Hierarchical fields mapped to backend names
        public type?: Nullable<string>,
        public subtype?: Nullable<string>,
        public category?: Nullable<string>,
        public scope?: Nullable<string>,
        public scopeDetail?: Nullable<string>, // scope_detail for backend
        public depositTypeId?: Nullable<string>,
        public depositSubtypeId?: Nullable<string>,
        public depositCategoryId?: Nullable<string>,
        public depositScopeId?: Nullable<string>,
    ) {
        this.type = type;
        this.subtype = subtype;
        this.category = category;
        this.scope = scope;
        this.scopeDetail = scopeDetail;
        this.depositTypeId = depositTypeId;
        this.depositSubtypeId = depositSubtypeId;
        this.depositCategoryId = depositCategoryId;
        this.depositScopeId = depositScopeId;
    }
}

export class Deposit extends CreateDeposit {
    // eslint-disable-next-line max-params
    constructor(
        public readonly id: number,
        concept: CreateDeposit["concept"],
        incomeLinkedAnotherProject: CreateDeposit["incomeLinkedAnotherProject"],
        amount: CreateDeposit["amount"],
        name: CreateDeposit["name"],
        depositDate: CreateDeposit["depositDate"],
        paymentMethod: Nullable<PaymentMethodTypeEnum>,
        ivaExemptionType: Nullable<IvaExemptTypeEnum>,
        irpfRetentionType: Nullable<IrpfRetentionTypeEnum>,
        irpfRetentionPercentageType: Nullable<IrpfRetentionPercentageTypeEnum>,
        reasonType: Nullable<ReasonTypeEnum>,
        identificationType: CreateDeposit["identificationType"],
        identification: CreateDeposit["identification"],
        street: CreateDeposit["street"],
        recipientIdentificationType: CreateDeposit["recipientIdentificationType"],
        recipientIdentification: CreateDeposit["recipientIdentification"],
        recipientStreet: CreateDeposit["recipientStreet"],
        financingOrganization: CreateDeposit["financingOrganization"],
        issuer: CreateDeposit["issuer"],
        recipient: CreateDeposit["recipient"],
        otherPaymentSource: CreateDeposit["otherPaymentSource"],
        expedientNumber: CreateDeposit["expedientNumber"],
        depositYear: CreateDeposit["depositYear"],
        tax: CreateDeposit["tax"],
        baseAmount: CreateDeposit["baseAmount"],
        entity: CreateDeposit["entity"],
        relatedProject: CreateDeposit["relatedProject"],
        relatedDocument: CreateDeposit["relatedDocument"],
        communicationDate: CreateDeposit["communicationDate"],
        invoiceIssueDate: CreateDeposit["invoiceIssueDate"],
        resolutionDate: CreateDeposit["resolutionDate"],
        executionDate: CreateDeposit["executionDate"],
        othersType: CreateDeposit["othersType"],
        observations: CreateDeposit["observations"],
        attachedDocumentName: CreateDeposit["attachedDocumentName"],
        type?: Nullable<string>,
        subtype?: Nullable<string>,
        category?: Nullable<string>,
        scope?: Nullable<string>,
        scopeDetail?: Nullable<string>,
        public splitDeposits?: Undefinable<SplitDeposit[]>,
    ) {
        super(
            concept,
            incomeLinkedAnotherProject,
            amount,
            name,
            depositDate,
            paymentMethod,
            ivaExemptionType,
            irpfRetentionType,
            irpfRetentionPercentageType,
            reasonType,
            identificationType,
            identification,
            street,
            recipientIdentificationType,
            recipientIdentification,
            recipientStreet,
            financingOrganization,
            issuer,
            recipient,
            otherPaymentSource,
            expedientNumber,
            depositYear,
            tax,
            baseAmount,
            entity,
            relatedProject,
            relatedDocument,
            communicationDate,
            invoiceIssueDate,
            resolutionDate,
            executionDate,
            othersType,
            observations,
            attachedDocumentName,
            type,
            subtype,
            category,
            scope,
            scopeDetail,
        );
        this.depositTypeId = type;
        this.depositSubtypeId = subtype;
        this.depositCategoryId = category;
        this.depositScopeId = scope;
    }
}
